/**
 * SkyNexa — enquiry form validation.
 *
 * Pure functions, no DOM, no network. This module is the contract that both
 * the browser (js/enquiry.js) and the unit suite (tests/unit/validation.test.js)
 * validate against, so the rules cannot drift between what is tested and what
 * ships.
 *
 * Client-side validation here is a usability feature, not a security control:
 * the receiving endpoint must re-validate. What this does guarantee is that a
 * visitor is told exactly what is wrong, per field, before anything is sent.
 *
 * @module validation
 */

/** Field length bounds, deliberately generous — an enquiry should never be rejected for being detailed. */
export const LIMITS = Object.freeze({
  name: { min: 2, max: 80 },
  company: { max: 120 },
  email: { max: 254 }, // RFC 5321 maximum path length.
  message: { min: 20, max: 4000 },
});

/**
 * Pragmatic email shape check. Deliberately not an RFC 5322 regex: those
 * accept addresses no mail provider will route and reject ones that work.
 * Anything that passes this is confirmed by the endpoint's own delivery.
 */
const EMAIL_PATTERN = /^[^\s@,;]+@[^\s@.,;]+(\.[^\s@.,;]+)+$/;

/** Disposable/role addresses we warn about but never hard-block. */
const ROLE_PREFIXES = ['noreply', 'no-reply', 'donotreply', 'postmaster'];

const isBlank = (value) => typeof value !== 'string' || value.trim().length === 0;

/**
 * Validate a single field.
 *
 * @param {string} field Field name, matching a key of the enquiry payload.
 * @param {unknown} value Raw value from the form.
 * @param {{ interests?: string[] }} [options] Allowed values for choice fields.
 * @returns {string|null} A human-readable error, or null when the field is valid.
 */
export function validateField(field, value, options = {}) {
  const text = typeof value === 'string' ? value.trim() : value;

  switch (field) {
    case 'name': {
      if (isBlank(value)) return 'Please enter your name.';
      if (text.length < LIMITS.name.min) return 'Please enter your full name.';
      if (text.length > LIMITS.name.max)
        return `Please keep your name under ${LIMITS.name.max} characters.`;
      return null;
    }

    case 'email': {
      if (isBlank(value)) return 'Please enter an email address so we can reply.';
      if (text.length > LIMITS.email.max) return 'That email address is too long.';
      if (!EMAIL_PATTERN.test(text))
        return 'Please enter a valid email address, for example name@company.com.';
      if (ROLE_PREFIXES.some((prefix) => text.toLowerCase().startsWith(prefix))) {
        return 'Please use an address that can receive replies.';
      }
      return null;
    }

    case 'company': {
      // Optional: individuals and side projects are explicitly welcome.
      if (isBlank(value)) return null;
      if (text.length > LIMITS.company.max) return 'Please shorten the organisation name.';
      return null;
    }

    case 'interest': {
      if (isBlank(value)) return 'Please choose what you need help with.';
      const allowed = options.interests;
      if (Array.isArray(allowed) && allowed.length > 0 && !allowed.includes(text)) {
        return 'Please choose one of the listed options.';
      }
      return null;
    }

    case 'budget':
    case 'timeline':
      // Both optional: not knowing the budget yet is not a reason to be turned away.
      return null;

    case 'message': {
      if (isBlank(value)) return 'Please describe what you would like built.';
      if (text.length < LIMITS.message.min) {
        return `Please add a little more detail — at least ${LIMITS.message.min} characters.`;
      }
      if (text.length > LIMITS.message.max) {
        return `Please keep the brief under ${LIMITS.message.max} characters, or attach details by email.`;
      }
      return null;
    }

    case 'consent': {
      if (value !== true && value !== 'on' && value !== 'true') {
        return 'Please confirm you are happy for us to reply to your enquiry.';
      }
      return null;
    }

    default:
      return null;
  }
}

/**
 * Validate a whole enquiry.
 *
 * @param {Record<string, unknown>} payload Raw form values.
 * @param {{ interests?: string[], honeypotField?: string, minFillSeconds?: number, elapsedSeconds?: number }} [options]
 * @returns {{ valid: boolean, errors: Record<string, string>, spam: boolean, firstInvalidField: string|null }}
 */
export function validateEnquiry(payload = {}, options = {}) {
  const {
    interests,
    honeypotField = 'website',
    minFillSeconds = 3,
    elapsedSeconds = Number.POSITIVE_INFINITY,
  } = options;

  const fields = [
    'name',
    'email',
    'company',
    'interest',
    'budget',
    'timeline',
    'message',
    'consent',
  ];
  const errors = {};

  for (const field of fields) {
    const error = validateField(field, payload[field], { interests });
    if (error) errors[field] = error;
  }

  // Two silent bot signals. Neither surfaces an error message: a bot should
  // not learn why it was rejected, and a human will never trip either one.
  const honeypotFilled = !isBlank(payload[honeypotField]);
  const submittedTooFast = elapsedSeconds < minFillSeconds;
  const spam = honeypotFilled || submittedTooFast;

  const firstInvalidField = fields.find((field) => field in errors) ?? null;

  return {
    valid: Object.keys(errors).length === 0 && !spam,
    errors,
    spam,
    firstInvalidField,
  };
}

/**
 * Normalise a validated enquiry into the payload sent to the endpoint.
 * Trims every string and drops empty optional fields so the notification
 * email does not carry blank rows.
 *
 * @param {Record<string, unknown>} payload
 * @returns {Record<string, string>}
 */
export function buildSubmission(payload = {}) {
  const output = {};
  for (const [key, value] of Object.entries(payload)) {
    if (typeof value !== 'string') {
      if (typeof value === 'boolean') output[key] = value ? 'yes' : 'no';
      continue;
    }
    const trimmed = value.trim();
    if (trimmed) output[key] = trimmed;
  }
  return output;
}
